import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { useSiteContent } from "@/hooks/useSiteContent";

const About = () => {
  const { t, isRTL, lang } = useLanguage();
  const ref = useReveal();
  const { data: about } = useSiteContent("about");
  const { data: statsDoc } = useSiteContent("stats");

  // Resolve dynamic overrides with static fallbacks
  const heading = (lang === "ar" ? about?.heading_ar : about?.heading_en) || t.about.heading;
  const contentHtml = lang === "ar" ? about?.content_ar : about?.content_en;
  const legacyParagraphs = lang === "ar" ? about?.paragraphs_ar : about?.paragraphs_en;

  const stats =
    (Array.isArray(statsDoc?.items) && statsDoc.items.length
      ? statsDoc.items.map((s) => ({
          value: s.value,
          label: lang === "ar" ? s.label_ar : s.label_en,
        }))
      : null) || t.about.stats;

  return (
    <section id="about" data-testid="about-section" className="relative section-y bg-ink">
      <div className="container-x">
        <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline" />
              <span className="overline">{t.about.overline}</span>
            </div>
            <h2
              className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance`}
              data-testid="about-heading"
            >
              {heading}
            </h2>
          </div>

          <div className={`lg:col-span-8 ${isRTL ? "font-arabic text-lg" : "text-base"} text-white/70 leading-[1.9]`}>
            {contentHtml ? (
              <div
                className="tiptap-content"
                data-testid="about-content-html"
                dir={isRTL ? "rtl" : "ltr"}
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            ) : (
              <div className="space-y-6">
                {(Array.isArray(legacyParagraphs) && legacyParagraphs.length
                  ? legacyParagraphs
                  : t.about.paragraphs
                ).map((p, i) => (
                  <p key={i} data-testid={`about-paragraph-${i}`}>
                    {p}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className="group bg-ink p-8 lg:p-12 hover:bg-ink-soft transition-colors duration-500"
            >
              <div
                className={`${isRTL ? "font-arabic font-black" : "font-serif"} text-gold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3 group-hover:text-gold-light transition-colors`}
              >
                {s.value}
              </div>
              <div className={`text-xs tracking-[0.2em] uppercase text-white/50 ${isRTL ? "font-arabic" : ""}`}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
